import React, { createContext, useContext, useState, useCallback } from 'react';
import { XIcon, WarningCircleIcon, CheckCircleIcon, InfoIcon } from '@phosphor-icons/react';

interface Toast {
    id: string;
    message: string;
    type: 'success' | 'error' | 'info';
}

interface ToastContextType {
    showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const showToast = useCallback((message: string, type: 'success' | 'error' | 'info' = 'info') => {
        const id = Date.now().toString();
        setToasts(prev => [...prev, { id, message, type }]);

        // Auto-dismiss after 5 seconds
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 5000);
    }, []);

    // Listen for global toast events (from contexts that can't use useToast directly)
    React.useEffect(() => {
        const handleToastEvent = (e: CustomEvent<{ message: string; type: 'success' | 'error' | 'info' }>) => {
            showToast(e.detail.message, e.detail.type);
        };
        window.addEventListener('app:toast', handleToastEvent as EventListener);
        return () => window.removeEventListener('app:toast', handleToastEvent as EventListener);
    }, [showToast]);

    const dismissToast = (id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    };

    const getIcon = (type: Toast['type']) => {
        switch (type) {
            case 'success': return <CheckCircleIcon className="w-5 h-5 text-green-400" />;
            case 'error': return <WarningCircleIcon className="w-5 h-5 text-red-400" />;
            default: return <InfoIcon className="w-5 h-5 text-blue-400" />;
        }
    };

    const getBorderColor = (type: Toast['type']) => {
        switch (type) {
            case 'success': return 'border-green-500/30';
            case 'error': return 'border-red-500/30';
            default: return 'border-blue-500/30';
        }
    };

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}

            {/* Toast Container */}
            <div className="fixed bottom-4 right-4 z-[200] flex flex-col gap-2 pointer-events-none">
                {toasts.map(toast => (
                    <div
                        key={toast.id}
                        className={`
                            pointer-events-auto flex items-center gap-3 px-4 py-3 
                            bg-zinc-900/90 backdrop-blur-md border ${getBorderColor(toast.type)}
                            rounded-lg shadow-2xl min-w-[300px] max-w-[400px]
                            animate-in slide-in-from-right-5 duration-300
                        `}
                    >
                        {getIcon(toast.type)}
                        <p className="flex-1 text-sm text-white">{toast.message}</p>
                        <button
                            onClick={() => dismissToast(toast.id)}
                            className="p-1 hover:bg-white/10 rounded transition-colors"
                        >
                            <XIcon className="w-4 h-4 text-zinc-400" />
                        </button>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within ToastProvider');
    }
    return context;
};
